// What the numbers know that the line does not.
//
// The book counts, for every position and every level, how often each move is
// played and what it scores. Two things in those counts are worth learning,
// and neither is in a line of moves:
//
//   Yours — a move hardly anybody at your level chooses that nonetheless
//   scores well for whoever does: a reply the opponent has not seen, a trap,
//   a line better than its reputation. It is worth more than the move
//   everybody plays, because the position it reaches is one the other side
//   has not prepared for.
//
//   Theirs — a move plenty of them play here that goes badly for them: the
//   Blackburne Shilling's 5.Nxe5, played by a fifth of them and scoring a
//   quarter. Knowing where the opponent goes wrong is worth as much as
//   knowing your own move, and no line of yours ever mentions it, because
//   your line prepares for their best.
//
// Both are judged against the move most of them play there, which must itself
// have been played enough to be a number. A count from thirty games is noise,
// and a rare move that scores well because it is a cheap trick the engine
// refutes is not something to put in a repertoire.

import type { BookMove, BookPosition } from "./BookPanel.tsx";
import { winChances } from "./scores.ts";

/** Played by at least this many at your level: fewer is not a number. */
const ENOUGH = 300;
/** Yours: chosen by at most this share of them — more is not a surprise. */
const RARE = 0.05;
/** And scoring at least this much better than the move everybody plays. */
const BETTER = 0.04;
/** The engine may not think it gives away this much of the position. */
const UNSOUND = 0.1;
/** Theirs: played by at least this share — fewer is not worth preparing. */
const COMMON = 0.08;
/** And scoring at least this much worse than the move most of them play. */
const WORSE = 0.06;
/** How many of each are offered at once. */
const MOST = 2;

/** Which games a move was judged on: the ones at your level, or — where
 *  those are too few to tell a mistake from noise — every level's. */
export type Pool = "band" | "all";

export interface Quirk {
  uci: string;
  san: string;
  /** Which games these numbers are. */
  pool: Pool;
  /** What share of games at your level play it, and what it scores for the
   *  side that plays it. */
  share: number;
  score: number;
  games: number;
  /** The move most of them play there, and what that scores. */
  usual: string;
  usualScore: number;
  /** How far this one is from it, always positive: better for a sly move,
   *  worse for a trap. */
  edge: number;
  kind: "sly" | "trap";
}

/** Your level first: it is the people you play who matter. Every level's
 *  games are the same book's, and are what is left to go on where your own
 *  level has too few. */
const POOLS: Pool[] = ["band", "all"];

const statOf = (move: BookMove, pool: Pool) => (pool === "band" ? move.band : move.all);

const scoreFor = (move: BookMove, white: boolean, pool: Pool): number | null => {
  const stats = statOf(move, pool);
  if (!stats || !stats.games) return null;
  const mine = white ? stats.white : stats.games - stats.white - stats.draws;
  return (mine + stats.draws / 2) / stats.games;
};

/** The evaluation after a move, from the mover's side. */
const after = (move: BookMove, white: boolean): number | null => {
  if (!move.white_eval) return null;
  const score = move.white_eval;
  const value = score.kind === "mate" ? (score.value > 0 ? 1200 : -1200) : score.value;
  return white ? value : -value;
};

/**
 * What the book knows about a position beyond the line: the rare moves that
 * score well (`kind: "sly"`) and the common ones that do not (`"trap"`),
 * whichever side is to move there. `known` are the moves the line already
 * shows on the board, which are left out: the point is what is not there.
 */
function judge(position: BookPosition, kind: "sly" | "trap", known: string[], most: number, pool: Pool, white: boolean): Quirk[] | null {
  const moves = (position.moves || []).filter((m) => (statOf(m, pool)?.games || 0) > 0);
  if (moves.length < 2) return null;
  const played = moves.slice().sort((a, b) => (statOf(b, pool)?.games || 0) - (statOf(a, pool)?.games || 0));
  const base = played[0]!;
  const usualScore = scoreFor(base, white, pool);
  // The baseline is a number or this pool cannot say anything: null, which
  // is not the same as having looked and found nothing.
  if (usualScore == null || (statOf(base, pool)?.games || 0) < ENOUGH) return null;
  const total = moves.reduce((n, m) => n + (statOf(m, pool)?.games || 0), 0);
  // What the engine makes of the position's best move: a rare move of your
  // own is judged against that, not against the average.
  const best = Math.max(...moves.map((m) => after(m, white) ?? -Infinity));
  const found: Quirk[] = [];
  for (const move of moves) {
    if (move.uci === base.uci || known.includes(move.uci)) continue;
    const games = statOf(move, pool)!.games;
    const share = total ? games / total : 0;
    const score = scoreFor(move, white, pool);
    if (games < ENOUGH || score == null) continue;
    if (kind === "sly") {
      if (share > RARE || score < usualScore + BETTER) continue;
      const eval_ = after(move, white);
      if (Number.isFinite(best) && eval_ != null && winChances({ kind: "cp", value: best }) - winChances({ kind: "cp", value: eval_ }) > UNSOUND) continue;
    } else {
      if (share < COMMON || score > usualScore - WORSE) continue;
    }
    const edge = kind === "sly" ? score - usualScore : usualScore - score;
    found.push({ uci: move.uci, san: move.san, pool, share, score, games, usual: base.san, usualScore, edge, kind });
  }
  return found.sort((a, b) => b.edge - a.edge).slice(0, most);
}

export function quirks(position: BookPosition | null, kind: "sly" | "trap", known: string[] = [], most = MOST): Quirk[] {
  if (!position || !position.in_book) return [];
  const white = String(position.fen).split(/\s+/)[1] !== "b";
  for (const pool of POOLS) {
    const found = judge(position, kind, known, most, pool, white);
    if (found) return found;
  }
  return [];
}

/**
 * How many games reach a position that not even every level's are enough to
 * judge — the reason a position with nothing shimmering on it has nothing to
 * say, rather than there being nothing to say about it. 0 where the book can
 * judge the position, whatever it then found.
 */
export function tooFew(position: BookPosition | null): number {
  if (!position || !position.in_book) return 0;
  const white = String(position.fen).split(/\s+/)[1] !== "b";
  for (const pool of POOLS) if (judge(position, "trap", [], 1, pool, white)) return 0;
  return position.games?.all || position.games?.band || 0;
}

/** What the board calls it, wherever it is named: on the position it was
 *  found in, and again once it has been played and is a move tried. Two
 *  names for the same move would read as two different things. */
export const quirkTitle = (kind: Quirk["kind"]): string => (kind === "sly" ? "Rare, and it scores" : "Where they go wrong");

/** And the same thing once it has been played: the end of a line the board
 *  pointed at, said in the words it was pointed at with, a tense on. */
export const quirkDone = (kind: Quirk["kind"]): string => (kind === "sly" ? "You took the rare line" : "They went wrong");

const pct = (x: number) => `${Math.round(100 * x)}%`;

/**
 * "4% play it and score 64%, against 54% for Nf3, over 1,200 games at your
 * level." Short, the games are left off: said again beside a move that has
 * just been played, the count was on the screen a moment ago and what is
 * wanted is the verdict.
 */
export function quirkSays(move: Quirk, short = false): string {
  const how = move.kind === "sly" ? `${pct(move.share)} play it and score ${pct(move.score)}` : `${pct(move.share)} of them play it and score ${pct(move.score)}`;
  const against = `, against ${pct(move.usualScore)} for ${move.usual}`;
  if (short) return `${how}${against}.`;
  const level = move.pool === "band" ? "at your level" : "at every level";
  return `${how}${against}, over ${move.games.toLocaleString("en")} games ${level}.`;
}
